import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { useAlarmRulesStatus } from "@/hooks/useAlarmRules";
import { cn } from "@/lib/cn";
import { formatRelativeAge } from "@/lib/format";

export type AckErrorDetail = { id?: string | null; error: string };

export type AckState =
  | { kind: "idle" }
  | { kind: "pending"; sinceMs: number }
  | { kind: "acknowledged"; loaded: number; errors: number; loadedAtMs: number; errorDetails: AckErrorDetail[] }
  | { kind: "edge_error"; errors: number; loadedAtMs: number; errorDetails: AckErrorDetail[] }
  | { kind: "timeout" };

interface Props {
  deviceId: string;
  /** If non-zero, indicates a pending save's wall-clock anchor. */
  pendingSince?: number;
  /** Called once the ACK arrives (or times out). */
  onAck?: (state: AckState) => void;
  /** Override the default 30 s pending → timeout horizon. */
  timeoutMs?: number;
}

const PENDING_TIMEOUT_MS_DEFAULT = 30_000;

export function AckStatusBadge({ deviceId, pendingSince, onAck, timeoutMs }: Props) {
  const polling = !!pendingSince;
  const { data: status } = useAlarmRulesStatus(deviceId, polling ? 2000 : false);
  const [state, setState] = useState<AckState>({ kind: "idle" });

  useEffect(() => {
    if (!pendingSince) {
      // Reflect last-known status without a pending anchor.
      if (status && status.loaded_at) {
        setState(
          status.errors.length > 0
            ? {
                kind: "edge_error",
                errors: status.errors.length,
                loadedAtMs: status.loaded_at,
                errorDetails: status.errors,
              }
            : {
                kind: "acknowledged",
                loaded: status.loaded,
                errors: status.errors.length,
                loadedAtMs: status.loaded_at,
                errorDetails: status.errors,
              },
        );
      } else {
        setState({ kind: "idle" });
      }
      return;
    }

    if (status && status.loaded_at >= pendingSince) {
      const next: AckState =
        status.errors.length > 0
          ? {
              kind: "edge_error",
              errors: status.errors.length,
              loadedAtMs: status.loaded_at,
              errorDetails: status.errors,
            }
          : {
              kind: "acknowledged",
              loaded: status.loaded,
              errors: status.errors.length,
              loadedAtMs: status.loaded_at,
              errorDetails: status.errors,
            };
      setState(next);
      onAck?.(next);
      return;
    }
    setState({ kind: "pending", sinceMs: pendingSince });
  }, [pendingSince, status, onAck]);

  useEffect(() => {
    if (state.kind !== "pending") return;
    const horizon = timeoutMs ?? PENDING_TIMEOUT_MS_DEFAULT;
    const expiresAt = state.sinceMs + horizon;
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      setState({ kind: "timeout" });
      onAck?.({ kind: "timeout" });
      return;
    }
    const t = window.setTimeout(() => {
      setState({ kind: "timeout" });
      onAck?.({ kind: "timeout" });
    }, remaining);
    return () => window.clearTimeout(t);
  }, [state, timeoutMs, onAck]);

  const cls = (tone: "ok" | "warn" | "err" | "muted" | "info") =>
    cn(
      "inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border font-semibold",
      tone === "ok" && "border-ok/40 bg-ok/10 text-ok",
      tone === "warn" && "border-warn/40 bg-warn/10 text-warn",
      tone === "err" && "border-critical/40 bg-critical/10 text-critical",
      tone === "muted" && "border-edge bg-surface-2/40 text-text-faint",
      tone === "info" && "border-accent/40 bg-accent/10 text-accent",
    );

  switch (state.kind) {
    case "idle":
      return <span className={cls("muted")}>No ACK</span>;
    case "pending":
      return (
        <span className={cls("info")}>
          <span className="size-1.5 rounded-full bg-accent animate-pulse" />
          Waiting for edge…
        </span>
      );
    case "acknowledged": {
      const ageMs = Math.max(0, Date.now() - state.loadedAtMs);
      const warnings = state.errorDetails.filter((e) => /unknown child rule id|unsupported schema/i.test(e.error));
      return (
        <DetailBadge
          className={cls("ok")}
          heading={warnings.length > 0 ? "Edge ACK — warnings" : "Edge ACK"}
          lines={
            warnings.length > 0
              ? warnings.map((e) => `${e.id ?? ""}: ${e.error}`)
              : [`${state.loaded} rule${state.loaded === 1 ? "" : "s"} loaded ${formatRelativeAge(ageMs)}`]
          }
        >
          ✔ Edge ACK ({state.loaded} rule{state.loaded === 1 ? "" : "s"},{" "}
          {formatRelativeAge(ageMs)})
          {warnings.length > 0 && (
            <span className="ml-1 text-warn">· {warnings.length} warn</span>
          )}
        </DetailBadge>
      );
    }
    case "edge_error":
      return (
        <DetailBadge
          className={cls("err")}
          heading={`Edge reported ${state.errors} error${state.errors === 1 ? "" : "s"}`}
          lines={state.errorDetails.map((e) => `${e.id ?? "<no id>"}: ${e.error}`)}
        >
          ✘ Edge reported {state.errors} error{state.errors === 1 ? "" : "s"}
        </DetailBadge>
      );
    case "timeout":
      return (
        <span className={cls("warn")}>
          ⚠ Edge offline — will apply when reconnected
        </span>
      );
  }
}

/**
 * Badge whose full detail lines open in a small click/tap popover (same
 * pattern as ui/HelpHint). A hover-only `title` is unreachable for touch
 * and keyboard operators (XTRA-07), so the badge itself is a button:
 * click / tap / Enter toggles the popover. The `title` attribute is kept
 * as a mouse-hover convenience — the popover carries the same lines.
 */
function DetailBadge({
  className,
  heading,
  lines,
  children,
}: {
  className: string;
  heading: string;
  lines: string[];
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((x) => !x);
        }}
        className={cn(className, "cursor-pointer")}
        title={lines.join("\n")}
        aria-expanded={open}
      >
        {children}
      </button>
      {open && (
        <>
          <button
            className="fixed inset-0 z-30 cursor-default"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            aria-hidden
          />
          <div className="absolute right-0 top-full z-40 mt-1 w-72 rounded-md border border-edge bg-surface p-3 text-left text-[11px] leading-relaxed text-text shadow-xl">
            <div className="flex items-start justify-between gap-2">
              <strong className="text-text">{heading}</strong>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                }}
                className="text-text-faint hover:text-text"
                aria-label="Close"
              >
                <X size={11} />
              </button>
            </div>
            <ul className="mt-1 space-y-1 text-text-muted">
              {lines.map((l, i) => (
                <li key={i} className="break-words font-mono text-[10px]">
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </span>
  );
}
